import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ColorTab } from "@/components/ui/color-tab";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { inr, fmtDate, statusColor } from "@/lib/utils-format";
import { Plus, Trash2, ArrowRight, Eye, IndianRupee, Loader2, Link as LinkIcon, FileDown, Mail, FileText, ClipboardList, ShoppingCart, Receipt, Pencil } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { ForceDeleteDialog } from "@/components/ForceDeleteDialog";
import { PosPaymentEditDialog } from "@/components/PosPaymentEditDialog";

const TAB_MAP = { quotation: "Quotations", proforma: "Proforma Invoices", order: "Sales Orders", invoice: "Invoices" };
const TAB_META = {
  quotation: { tone: "blue", icon: FileText },
  proforma:  { tone: "purple", icon: ClipboardList },
  order:     { tone: "orange", icon: ShoppingCart },
  invoice:   { tone: "green", icon: Receipt },
};
const NEXT_STEP = { quotation: "proforma", proforma: "order", order: "invoice" };

const emptyItem = { product_id: "", name: "", description: "", quantity: 1, unit: "pcs", unit_price: 0, gst_rate: 5, discount_pct: 0 };

export default function SalesPage() {
  const { user } = useAuth();
  const isSuperAdmin = (user?.role_id || user?.role) === "super_admin";
  const [tab, setTab] = useState("quotation");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [payMethod, setPayMethod] = useState("all");
  const PAY_FILTERS = [["all", "All methods"], ["cash", "Cash"], ["gpay", "GPay"], ["upi", "UPI"], ["phonepe", "PhonePe"], ["card", "Card"], ["wallet", "Wallet"], ["credit", "Credit (udhaar)"]];
  const [docs, setDocs] = useState([]);
  const collected = payMethod === "all" ? null : docs.filter((d) => d.status !== "voided").reduce((s, d) =>
    s + (d.payments || []).filter((p) => (p.method || "").toLowerCase() === payMethod).reduce((a, p) => a + Number(p.amount || 0), 0), 0);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [payOpen, setPayOpen] = useState(false);
  const [posPayEdit, setPosPayEdit] = useState(false);
  const [payForm, setPayForm] = useState({ amount: 0, method: "cash", reference: "" });
  const [branches, setBranches] = useState([]);
  const [form, setForm] = useState({
    doc_type: "quotation",
    customer_id: "",
    branch_id: "",
    is_interstate: false,
    notes: "",
    valid_until: "",
    doc_date: new Date().toISOString().slice(0, 10),
    items: [{ ...emptyItem }],
  });

  const load = async () => {
    const [d, c, p, b] = await Promise.all([
      api.get("/sales-docs", { params: { doc_type: tab, ...(dateFrom ? { date_from: dateFrom } : {}), ...(dateTo ? { date_to: dateTo } : {}), ...(tab === "invoice" && payMethod !== "all" ? { payment_method: payMethod } : {}) } }),
      api.get("/customers"),
      api.get("/products"),
      api.get("/branches").catch(() => ({ data: [] })),
    ]);
    setDocs(d.data);
    setCustomers(c.data);
    setProducts(p.data);
    const locs = (b.data || []).filter((x) => ["warehouse", "retail_showroom"].includes(x.type));
    locs.sort((a, c2) => (a.type === "warehouse" ? -1 : c2.type === "warehouse" ? 1 : (a.name || "").localeCompare(c2.name || "")));
    setBranches(locs);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab, dateFrom, dateTo, payMethod]);

  const addItem = () => setForm((f) => ({ ...f, items: [...f.items, { ...emptyItem }] }));
  const removeItem = (i) => setForm((f) => ({ ...f, items: f.items.filter((_, idx) => idx !== i) }));
  const updateItem = (i, key, value) => {
    setForm((f) => {
      const items = [...f.items];
      if (key === "product_id") {
        const prod = products.find((p) => p.id === value);
        items[i] = { ...items[i], product_id: value };
        if (prod) {
          items[i].name = prod.name;
          items[i].unit = prod.unit;
          items[i].unit_price = prod.sale_price;
          items[i].gst_rate = prod.gst_rate;
        }
      } else {
        items[i] = { ...items[i], [key]: key === "name" || key === "description" || key === "unit" ? value : Number(value) || 0 };
      }
      return { ...f, items };
    });
  };

  const totals = React.useMemo(() => {
    let sub = 0, gst = 0;
    form.items.forEach((it) => {
      const gross = Number(it.quantity) * Number(it.unit_price);
      const disc = gross * (Number(it.discount_pct || 0) / 100);
      const net = gross - disc;
      sub += net;
      gst += net * (Number(it.gst_rate || 0) / 100);
    });
    return { sub, gst, grand: sub + gst };
  }, [form.items]);

  const submit = async () => {
    if (!form.customer_id) return toast.error("Select a customer");
    if (tab === "invoice" && !form.branch_id) return toast.error("Select the branch this invoice sells from");
    if (form.items.length === 0 || !form.items[0].name) return toast.error("Add at least one item");
    try {
      if (editingId) {
        await api.patch(`/sales-docs/${editingId}`, { ...form });
        toast.success(`${TAB_MAP[tab].slice(0, -1)} updated`);
      } else {
        await api.post("/sales-docs", { ...form, doc_type: tab });
        toast.success(`${TAB_MAP[tab].slice(0, -1)} created`);
      }
      setOpen(false); setEditingId(null);
      setForm({ doc_type: tab, customer_id: "", branch_id: "", is_interstate: false, notes: "", valid_until: "", doc_date: new Date().toISOString().slice(0, 10), items: [{ ...emptyItem }] });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const openEdit = (d) => {
    setForm({
      doc_type: d.doc_type, customer_id: d.customer_id, branch_id: d.branch_id || "", is_interstate: d.is_interstate,
      notes: d.notes || "", valid_until: d.valid_until || "",
      doc_date: d.doc_date || (d.created_at || "").slice(0, 10),
      items: (d.items || []).map((it) => ({ ...it })),
    });
    setEditingId(d.id); setOpen(true);
  };

  const view = async (d) => {
    const { data } = await api.get(`/sales-docs/${d.id}`);
    setViewing(data);
  };

  const convert = async (d) => {
    const target = NEXT_STEP[d.doc_type];
    if (!target) return;
    if (!window.confirm(`Convert ${d.number} to ${target}?`)) return;
    try {
      const { data } = await api.post(`/sales-docs/${d.id}/convert`, null, { params: { target_type: target } });
      toast.success(`Converted to ${data.number}`);
      setTab(target);
      setViewing(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const [deleting, setDeleting] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const remove = (d) => setDeleting(d);
  const deleteEffects = (d) => {
    if (!d) return [];
    const isInvoice = d.doc_type === "invoice";
    const paid = Number(d.amount_paid || 0);
    const payCount = d.payments?.length;
    const qty = (d.items || []).reduce((a, it) => a + Number(it.quantity || 0), 0);
    const isPos = d.channel === "pos";
    const posPaid = (d.payments || []).reduce((a, p) => a + Number(p.amount || 0), 0);
    const where = d.branch_name ? ` at ${d.branch_name}` : "";
    if (isPos) {
      return [
        d.status !== "voided" && qty > 0 && `Return ${qty} unit(s) to showroom stock${where} (incl. FEFO batches) via the POS void logic`,
        d.status !== "voided" && posPaid > 0 && `Reverse the embedded ${inr(posPaid)} payment(s) and the pos_sale ledger entry (Cash/UPI ↔ Sales/GST)`,
        d.status !== "voided" && "Refund any loyalty points earned on this sale",
        d.status === "voided" && "Already voided — stock and ledger were returned then; only the record is removed",
      ];
    }
    return [
      isInvoice && paid > 0 && `Reverse ${inr(paid)} in customer payment(s)${payCount ? ` (${payCount})` : ""} — mirrored receipt reversals posted, payments voided, Paid Amount reset to ₹0`,
      isInvoice && d.stock_deducted !== false && qty > 0 && `Restore ${qty} unit(s) of stock${where || " (legacy: global stock)"} deducted by this invoice`,
      isInvoice && `Reverse the sales_doc_invoice ledger entry of ${inr(d.grand_total)} (Accounts Receivable / Sales / GST)`,
    ];
  };
  const confirmDelete = async () => {
    if (!deleting) return;
    setDeleteBusy(true);
    try {
      const { data } = await api.delete(`/sales-docs/${deleting.id}`);
      const n = data?.payments_reversed?.count || 0;
      toast.success(`${deleting.number} deleted${n ? ` — ${n} payment(s) reversed` : ""}`);
      setDeleting(null); setViewing(null); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete");
    } finally { setDeleteBusy(false); }
  };

  const openPay = (inv) => {
    setPayForm({ amount: Math.max(0, (inv.grand_total || 0) - (inv.amount_paid || 0)), method: "cash", reference: "" });
    setPayOpen(true);
  };

  const submitPay = async () => {
    try {
      await api.post("/payments", { invoice_id: viewing.id, amount: Number(payForm.amount), method: payForm.method, reference: payForm.reference });
      toast.success("Payment recorded");
      setPayOpen(false);
      const { data } = await api.get(`/sales-docs/${viewing.id}`);
      setViewing(data);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="space-y-6" data-testid="sales-page">
      <ForceDeleteDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)} title={`Force-delete ${deleting?.doc_type === "invoice" ? "Invoice" : "Sales document"}`}
        subject={deleting?.number} effects={deleteEffects(deleting)} onConfirm={confirmDelete} busy={deleteBusy} testid="sales-force-delete" />
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="uc-label">Revenue</div>
          <h1 className="font-[Outfit] text-3xl sm:text-4xl font-semibold tracking-tight mt-1">Sales</h1>
        </div>
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setEditingId(null); setForm({ doc_type: tab, customer_id: "", branch_id: "", is_interstate: false, notes: "", valid_until: "", doc_date: new Date().toISOString().slice(0, 10), items: [{ ...emptyItem }] }); } }}>
          <DialogTrigger asChild>
            <Button data-testid="sales-add-btn" className="bg-[#C05621] hover:bg-[#9C4221] text-white">
              <Plus className="w-4 h-4 mr-1.5" /> New {TAB_MAP[tab].slice(0, -1)}
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-4xl bg-white max-h-[90vh] overflow-y-auto" data-testid="sales-form-dialog">
            <DialogHeader>
              <DialogTitle className="font-[Outfit]">{editingId ? "Edit" : "New"} {TAB_MAP[tab].slice(0, -1)}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Customer *</Label>
                  <Select value={form.customer_id} onValueChange={(v) => setForm({ ...form, customer_id: v })}>
                    <SelectTrigger data-testid="sales-customer-select"><SelectValue placeholder="Select customer" /></SelectTrigger>
                    <SelectContent className="bg-white max-h-72">
                      {customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} {c.company ? `· ${c.company}` : ""}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Sells from branch {tab === "invoice" ? "*" : "(optional)"}</Label>
                  <Select value={form.branch_id} onValueChange={(v) => setForm({ ...form, branch_id: v })}>
                    <SelectTrigger data-testid="sales-branch-select"><SelectValue placeholder="HO Warehouse or a showroom" /></SelectTrigger>
                    <SelectContent className="bg-white max-h-72">
                      {branches.map((b) => (
                        <SelectItem key={b.id} value={b.id} data-testid={`sales-branch-option-${b.id}`}>
                          {b.type === "warehouse" ? `HO Warehouse (${b.name})` : b.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {tab === "invoice" && <p className="text-[11px] text-[#8B7355] mt-1">Stock is deducted from this location's branch stock.</p>}
                </div>
                <div className="flex items-center gap-3 pt-6">
                  <Switch checked={form.is_interstate} onCheckedChange={(v) => setForm({ ...form, is_interstate: v })} data-testid="sales-interstate-toggle" />
                  <Label className="cursor-pointer">Inter-state (IGST) — else CGST+SGST</Label>
                </div>
                <div>
                  <Label>{tab === "invoice" ? "Sale Date *" : "Document Date *"}</Label>
                  <Input type="date" value={form.doc_date} onChange={(e) => setForm({ ...form, doc_date: e.target.value })} data-testid="sales-doc-date" />
                  <p className="text-[11px] text-[#8B7355] mt-1">Defaults to today — back-date or post-date freely.</p>
                </div>
                <div>
                  <Label>Valid Until</Label>
                  <Input type="date" value={form.valid_until} onChange={(e) => setForm({ ...form, valid_until: e.target.value })} />
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="uc-label">Line Items</div>
                  <Button size="sm" variant="outline" onClick={addItem} data-testid="sales-add-item-btn"><Plus className="w-3 h-3 mr-1" /> Add row</Button>
                </div>
                <div className="border border-[#EAE5D9] rounded-md overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-[#F7F2E8] uc-label">
                      <tr>
                        <th className="text-left px-2 py-2 w-40">Product (optional)</th>
                        <th className="text-left px-2 py-2">Item / Description</th>
                        <th className="text-right px-2 py-2 w-20">Qty</th>
                        <th className="text-left px-2 py-2 w-20">Unit</th>
                        <th className="text-right px-2 py-2 w-28">Price</th>
                        <th className="text-right px-2 py-2 w-20">GST %</th>
                        <th className="text-right px-2 py-2 w-20">Disc %</th>
                        <th className="text-right px-2 py-2 w-28">Line Total</th>
                        <th className="w-8"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {form.items.map((it, i) => {
                        const gross = Number(it.quantity) * Number(it.unit_price);
                        const disc = gross * (Number(it.discount_pct || 0) / 100);
                        const net = gross - disc;
                        const gst = net * (Number(it.gst_rate || 0) / 100);
                        return (
                          <tr key={i} className="border-t border-[#EAE5D9]">
                            <td className="px-2 py-1.5">
                              <Select value={it.product_id || ""} onValueChange={(v) => updateItem(i, "product_id", v)}>
                                <SelectTrigger className="h-8" data-testid={`sales-item-product-${i}`}><SelectValue placeholder="—" /></SelectTrigger>
                                <SelectContent className="bg-white max-h-72">
                                  {products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} · {inr(p.sale_price)}</SelectItem>)}
                                </SelectContent>
                              </Select>
                            </td>
                            <td className="px-2 py-1.5"><Input value={it.name} onChange={(e) => updateItem(i, "name", e.target.value)} placeholder="Item name" className="h-8" data-testid={`sales-item-name-${i}`} /></td>
                            <td className="px-2 py-1.5"><Input type="number" value={it.quantity} onChange={(e) => updateItem(i, "quantity", e.target.value)} className="h-8 text-right" /></td>
                            <td className="px-2 py-1.5"><Input value={it.unit} onChange={(e) => updateItem(i, "unit", e.target.value)} className="h-8" /></td>
                            <td className="px-2 py-1.5"><Input type="number" value={it.unit_price} onChange={(e) => updateItem(i, "unit_price", e.target.value)} className="h-8 text-right" /></td>
                            <td className="px-2 py-1.5"><Input type="number" value={it.gst_rate} onChange={(e) => updateItem(i, "gst_rate", e.target.value)} className="h-8 text-right" /></td>
                            <td className="px-2 py-1.5"><Input type="number" value={it.discount_pct} onChange={(e) => updateItem(i, "discount_pct", e.target.value)} className="h-8 text-right" /></td>
                            <td className="px-2 py-1.5 text-right font-medium">{inr(net + gst)}</td>
                            <td className="px-2 py-1.5">
                              {form.items.length > 1 && (
                                <button onClick={() => removeItem(i)} className="text-red-500 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div><Label>Notes</Label><Textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
                <div className="bg-[#F7F2E8] rounded-md p-4 space-y-1.5 text-sm">
                  <div className="flex justify-between"><span className="text-[#5C4033]">Subtotal</span><span className="font-medium">{inr(totals.sub)}</span></div>
                  <div className="flex justify-between"><span className="text-[#5C4033]">GST ({form.is_interstate ? "IGST" : "CGST + SGST"})</span><span className="font-medium">{inr(totals.gst)}</span></div>
                  <div className="flex justify-between pt-2 border-t border-[#EAE5D9]"><span className="font-semibold">Grand Total</span><span className="font-[Outfit] font-semibold text-lg text-[#C05621]">{inr(totals.grand)}</span></div>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={submit} data-testid="sales-save-btn" className="bg-[#C05621] hover:bg-[#9C4221] text-white">{editingId ? "Save Changes" : "Create"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex flex-wrap h-auto bg-transparent p-0 gap-0">
          {Object.entries(TAB_MAP).map(([k, v]) => (
            <ColorTab key={k} value={k} tone={TAB_META[k]?.tone || "slate"} icon={TAB_META[k]?.icon} testid={`sales-tab-${k}`}>{v}</ColorTab>
          ))}
        </TabsList>

        <div className="mt-3 flex flex-wrap items-end gap-3 bg-white border border-[#EAE5D9] rounded-lg px-4 py-3" data-testid="sales-date-filter">
          <div>
            <Label className="text-xs">From Date</Label>
            <Input type="date" value={dateFrom} max={dateTo || undefined} onChange={(e) => setDateFrom(e.target.value)} className="h-9 w-44" data-testid="sales-date-from" />
          </div>
          <div>
            <Label className="text-xs">To Date</Label>
            <Input type="date" value={dateTo} min={dateFrom || undefined} onChange={(e) => setDateTo(e.target.value)} className="h-9 w-44" data-testid="sales-date-to" />
          </div>
          {tab === "invoice" && (
            <div>
              <Label className="text-xs">Payment method</Label>
              <Select value={payMethod} onValueChange={setPayMethod}>
                <SelectTrigger className="h-9 w-44 bg-white" data-testid="sales-pay-method-filter"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  {PAY_FILTERS.map(([k, l]) => <SelectItem key={k} value={k} data-testid={`sales-pay-method-${k}`}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          {(dateFrom || dateTo || (tab === "invoice" && payMethod !== "all")) && (
            <Button variant="ghost" size="sm" onClick={() => { setDateFrom(""); setDateTo(""); setPayMethod("all"); }} data-testid="sales-date-clear" className="h-9 text-[#C05621]">Clear filters</Button>
          )}
          <div className="ml-auto text-right text-xs text-[#8B7355]" data-testid="sales-date-count">
            <div>{docs.length} record{docs.length === 1 ? "" : "s"}{(dateFrom || dateTo) ? " in range" : ""}</div>
            {collected !== null && (
              <div className="font-semibold text-[#2C1810]" data-testid="sales-pay-method-total">
                Collected via {PAY_FILTERS.find(([k]) => k === payMethod)?.[1]}: {inr(collected)}
              </div>
            )}
          </div>
        </div>

        {Object.keys(TAB_MAP).map((k) => (
          <TabsContent key={k} value={k} className="mt-4">
            <div className="bg-white border border-[#EAE5D9] rounded-lg overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left uc-label border-b border-[#EAE5D9]">
                    <th className="px-4 py-3">Number</th>
                    <th className="px-4 py-3">Customer</th>
                    {k === "invoice" && <th className="px-4 py-3">Branch</th>}
                    <th className="px-4 py-3">Status</th>
                    {k === "invoice" && <th className="px-4 py-3">Payment</th>}
                    <th className="px-4 py-3 text-right">Total</th>
                    <th className="px-4 py-3">Date</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {docs.map((d) => (
                    <tr key={d.id} className="border-b border-[#EAE5D9] last:border-0 hover:bg-[#FDFBF7]" data-testid={`sales-row-${d.id}`}>
                      <td className="px-4 py-3 font-medium">{d.number}</td>
                      <td className="px-4 py-3">{d.customer_snapshot?.name}</td>
                      {k === "invoice" && (
                        <td className="px-4 py-3 text-xs" data-testid={`sales-branch-${d.id}`}>
                          {d.branch_name || <span className="text-[#8B7355]">—</span>}
                          {d.channel === "pos" && <Badge variant="outline" className="ml-1 text-[10px] bg-[#FDF3EC] text-[#C05621] border-[#F0C9B0]">POS</Badge>}
                        </td>
                      )}
                      <td className="px-4 py-3"><Badge variant="outline" className={`capitalize ${statusColor(d.status)}`}>{d.status}</Badge></td>
                      {k === "invoice" && (
                        <td className="px-4 py-3"><Badge variant="outline" className={`capitalize ${statusColor(d.payment_status)}`}>{d.payment_status}</Badge></td>
                      )}
                      <td className="px-4 py-3 text-right font-semibold">{inr(d.grand_total)}</td>
                      <td className="px-4 py-3 text-[#8B7355]" data-testid={`sales-row-date-${d.id}`}>{fmtDate(d.doc_date || d.created_at)}</td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex justify-end gap-1">
                          <button onClick={() => view(d)} data-testid={`sales-view-btn-${d.id}`} className="p-1.5 hover:bg-[#F7F2E8] rounded"><Eye className="w-4 h-4" /></button>
                          {NEXT_STEP[d.doc_type] && d.status !== "converted" && (
                            <button onClick={() => convert(d)} data-testid={`sales-convert-btn-${d.id}`} className="p-1.5 hover:bg-[#F7F2E8] rounded text-[#C05621]" title={`Convert to ${NEXT_STEP[d.doc_type]}`}>
                              <ArrowRight className="w-4 h-4" />
                            </button>
                          )}
                          {isSuperAdmin && (
                            <>
                              <button onClick={() => openEdit(d)} data-testid={`sales-edit-btn-${d.id}`} className="p-1.5 hover:bg-blue-50 text-blue-600 rounded" title="Edit (Super Admin)"><Pencil className="w-4 h-4" /></button>
                              <button onClick={() => remove(d)} data-testid={`sales-delete-btn-${d.id}`} className="p-1.5 hover:bg-red-50 text-red-600 rounded" title="Delete (Super Admin)"><Trash2 className="w-4 h-4" /></button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                  {docs.length === 0 && (
                    <tr><td colSpan={7} className="text-center py-10 text-[#8B7355]">No {TAB_MAP[k].toLowerCase()} yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </TabsContent>
        ))}
      </Tabs>

      {/* View dialog */}
      <Dialog open={!!viewing} onOpenChange={(o) => !o && setViewing(null)}>
        <DialogContent className="max-w-3xl bg-white max-h-[90vh] overflow-y-auto" data-testid="sales-view-dialog">
          {viewing && (
            <>
              <DialogHeader>
                <div className="flex items-center justify-between gap-3">
                  <DialogTitle className="font-[Outfit] text-2xl">{viewing.number}</DialogTitle>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className={`capitalize ${statusColor(viewing.status)}`}>{viewing.status}</Badge>
                    <Button
                      size="sm"
                      variant="outline"
                      data-testid="sales-download-pdf-btn"
                      onClick={() => {
                        const token = localStorage.getItem("bmf_token");
                        // Open PDF in new tab with auth header via fetch → blob
                        fetch(`${API}/sales-docs/${viewing.id}/pdf`, { headers: { Authorization: `Bearer ${token}` } })
                          .then((r) => r.blob())
                          .then((b) => {
                            const url = URL.createObjectURL(b);
                            window.open(url, "_blank");
                            setTimeout(() => URL.revokeObjectURL(url), 60000);
                          })
                          .catch(() => toast.error("PDF download failed"));
                      }}
                    >
                      <FileDown className="w-3.5 h-3.5 mr-1" /> PDF
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      data-testid="sales-email-btn"
                      onClick={async () => {
                        try {
                          await api.post(`/sales-docs/${viewing.id}/email`);
                          toast.success(`Emailed to ${viewing.customer_snapshot?.email}`);
                        } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
                      }}
                    >
                      <Mail className="w-3.5 h-3.5 mr-1" /> Email
                    </Button>
                    {["quotation", "proforma", "invoice"].includes(viewing.doc_type) && (
                      <Button
                        size="sm"
                        variant="outline"
                        data-testid="sales-copy-public-link-btn"
                        onClick={() => {
                          const link = `${window.location.origin}/q/${viewing.id}`;
                          navigator.clipboard.writeText(link);
                          toast.success("Public link copied — share with customer");
                        }}
                      >
                        <LinkIcon className="w-3.5 h-3.5 mr-1" /> Copy public link
                      </Button>
                    )}
                  </div>
                </div>
              </DialogHeader>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <div className="uc-label mb-1">Bill To</div>
                    <div className="font-semibold">{viewing.customer_snapshot?.name}</div>
                    <div className="text-[#5C4033]">{viewing.customer_snapshot?.company}</div>
                    <div className="text-[#5C4033]">{viewing.customer_snapshot?.email}</div>
                    <div className="text-[#5C4033]">{viewing.customer_snapshot?.phone}</div>
                    {viewing.customer_snapshot?.gstin && <div className="text-[#5C4033]">GSTIN: {viewing.customer_snapshot.gstin}</div>}
                  </div>
                  <div className="text-right">
                    <div className="uc-label mb-1">Details</div>
                    <div>Type: <span className="capitalize font-medium">{viewing.doc_type}</span></div>
                    <div>Date: {fmtDate(viewing.doc_date || viewing.created_at)}</div>
                    {viewing.valid_until && <div>Valid until: {fmtDate(viewing.valid_until)}</div>}
                    <div>{viewing.is_interstate ? "Inter-state (IGST)" : "Intra-state (CGST+SGST)"}</div>
                  </div>
                </div>

                <div className="border border-[#EAE5D9] rounded-md overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-[#F7F2E8] uc-label">
                      <tr>
                        <th className="text-left px-3 py-2">Item</th>
                        <th className="text-right px-3 py-2">Qty</th>
                        <th className="text-right px-3 py-2">Price</th>
                        <th className="text-right px-3 py-2">GST</th>
                        <th className="text-right px-3 py-2">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {viewing.items?.map((it, i) => {
                        const gross = it.quantity * it.unit_price;
                        const net = gross - gross * (it.discount_pct / 100);
                        const gst = net * (it.gst_rate / 100);
                        return (
                          <tr key={i} className="border-t border-[#EAE5D9]">
                            <td className="px-3 py-2">{it.name}{it.description ? <div className="text-xs text-[#8B7355]">{it.description}</div> : null}</td>
                            <td className="px-3 py-2 text-right">{it.quantity} {it.unit}</td>
                            <td className="px-3 py-2 text-right">{inr(it.unit_price)}</td>
                            <td className="px-3 py-2 text-right">{it.gst_rate}%</td>
                            <td className="px-3 py-2 text-right font-medium">{inr(net + gst)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                <div className="flex justify-end">
                  <div className="w-64 space-y-1 text-sm">
                    <div className="flex justify-between"><span>Subtotal</span><span>{inr(viewing.subtotal)}</span></div>
                    {viewing.is_interstate ? (
                      <div className="flex justify-between"><span>IGST</span><span>{inr(viewing.igst)}</span></div>
                    ) : (
                      <>
                        <div className="flex justify-between"><span>CGST</span><span>{inr(viewing.cgst)}</span></div>
                        <div className="flex justify-between"><span>SGST</span><span>{inr(viewing.sgst)}</span></div>
                      </>
                    )}
                    <div className="flex justify-between border-t border-[#EAE5D9] pt-1.5 font-[Outfit] font-semibold text-lg text-[#C05621]"><span>Grand Total</span><span>{inr(viewing.grand_total)}</span></div>
                    {viewing.doc_type === "invoice" && (
                      <>
                        <div className="flex justify-between text-emerald-700"><span>Paid</span><span>{inr(viewing.amount_paid || 0)}</span></div>
                        <div className="flex justify-between text-red-700"><span>Due</span><span>{inr((viewing.grand_total || 0) - (viewing.amount_paid || 0))}</span></div>
                      </>
                    )}
                  </div>
                </div>

                {viewing.doc_type === "invoice" && (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <div className="uc-label">Payments ({viewing.payments?.length || 0})</div>
                      {viewing.channel === "pos" ? (
                        isSuperAdmin && viewing.status !== "voided" && (
                          <Button size="sm" variant="outline" onClick={() => setPosPayEdit(true)} data-testid="sales-pos-edit-payment-btn" className="border-[#C05621] text-[#C05621] hover:bg-[#FDF3EC]">
                            <Pencil className="w-3.5 h-3.5 mr-1" /> Edit payment method
                          </Button>
                        )
                      ) : (
                        <Button size="sm" onClick={() => openPay(viewing)} data-testid="sales-record-payment-btn" className="bg-emerald-600 hover:bg-emerald-700 text-white">
                          <IndianRupee className="w-3.5 h-3.5 mr-1" /> Record Payment
                        </Button>
                      )}
                    </div>
                    {viewing.payments?.length > 0 && (
                      <div className="border border-[#EAE5D9] rounded-md">
                        {viewing.payments.map((p) => (
                          <div key={p.id} className="flex items-center justify-between px-3 py-2 border-b border-[#EAE5D9] last:border-0 text-sm">
                            <span>{fmtDate(p.paid_at || p.at)}</span>
                            <span className="capitalize text-[#5C4033]">{p.method}</span>
                            {p.reference && <span className="text-xs text-[#8B7355]">Ref: {p.reference}</span>}
                            <span className="font-semibold text-emerald-700">{inr(p.amount)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Payment dialog */}
      <PosPaymentEditDialog sale={viewing} open={posPayEdit} onClose={() => setPosPayEdit(false)} onSaved={(d) => { setViewing(d); load(); }} />
      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent className="max-w-md bg-white">
          <DialogHeader><DialogTitle className="font-[Outfit]">Record Payment</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div><Label>Amount (₹) *</Label><Input type="number" value={payForm.amount} onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })} data-testid="payment-amount-input" /></div>
            <div>
              <Label>Method</Label>
              <Select value={payForm.method} onValueChange={(v) => setPayForm({ ...payForm, method: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  {["cash", "upi", "bank_transfer", "cheque", "card"].map((m) => <SelectItem key={m} value={m} className="capitalize">{m.replace("_", " ")}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Reference</Label><Input value={payForm.reference} onChange={(e) => setPayForm({ ...payForm, reference: e.target.value })} placeholder="UPI ref / cheque no." /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayOpen(false)}>Cancel</Button>
            <Button onClick={submitPay} data-testid="payment-save-btn" className="bg-emerald-600 hover:bg-emerald-700 text-white">Record</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
